import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../../services/presentacionesApi';
import { useDownloadVisitaActaMutation } from '../../../../services/visitasApi';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { enqueueSnackbar } from 'notistack';
import { buildActaInterviewedData } from '../../utils/actaPayload';

export const useViewExecutionVisitDownloads = (selected: any) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('visitas');
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [getPresentacionRGRLPDFbyId] = useLazyGetPresentacionRGRLPDFbyIdQuery();
    const handleDownloadActa = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadActa.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selected.idVisita || selected.id,
                idTipoVisita: selected?.idTipoVisita,
                tipoActa: 1,
                datos: buildActaInterviewedData(selected)
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = `${t('table.actions.downloadActaFilename')}.pdf`;
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadActa.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t('alerts.downloadActa.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch (parseError) {}

            dispatch(
                showAlert({
                    icon: 'error',
                    message: errorMessage
                })
            );
        }
    };

    const handleDownloadVerosimilitud = async () => {
        if (!selected) return;

        try {
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVerosimilitud.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selected.idVisita || selected.id,
                idTipoVisita: selected?.idTipoVisita,
                tipoActa: 2
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `ActaVerosimilitud.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    icon: 'error',
                    message: t('alerts.downloadVerosimilitud.error')
                })
            );
        }
    };

    const handleDownloadRgrl = async () => {
        if (!selected?.idPresentacion) {
            dispatch(showAlert({ icon: 'error', message: t('alerts.downloadRgrl.notFoundId') }));
            return;
        }

        try {
            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadRgrl.generating'),
                    loading: true
                })
            );

            const response = await getPresentacionRGRLPDFbyId({
                idPresentacion: selected?.idPresentacion,
                typeId: 2
            }).unwrap();

            if (response?.data?.presignedUrls?.length > 0) {
                const url = response.data.presignedUrls[0];
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `PresentacionRGRL.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    icon: 'error',
                    message: t('alerts.downloadRgrl.error')
                })
            );
        }
    };

    return { handleDownloadVerosimilitud, handleDownloadRgrl, handleDownloadActa };
};
